import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { CcqArcModes, type CcqArcMode } from './ccqStatusArcModes'
import styles from './CcqStatusArc.module.scss'

const RADIUS = 11
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

// Reduced motion slows the spin rather than stopping it: it's the only sign of life.
const SPIN_MS = 900
const SPIN_REDUCED_MS = 2400

const SETTLE_MIN_MS = 120
const SETTLE_MAX_MS = 450

const spinDuration = (): number => (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? SPIN_REDUCED_MS : SPIN_MS)

const angleOf = (spin: Animation, duration: number): number => {
  const time = Number(spin.currentTime ?? 0)
  return ((time % duration) / duration) * 360
}

interface CcqStatusArcProps {
  mode: CcqArcMode
  fraction: number
  // While a batch's count animates, the arc rests at 12 o'clock so it grows from the top.
  isCounting: boolean
}

export const CcqStatusArc: React.FC<CcqStatusArcProps> = ({ mode, fraction, isCounting }) => {
  const progress = Math.max(Math.min(fraction, 1), 0)
  const filled = CIRCUMFERENCE * progress
  const isSpinner = mode === CcqArcModes.SPINNER
  const isHeld = mode === CcqArcModes.PROGRESS && isCounting
  const isDone = mode === CcqArcModes.DONE

  const rotorRef = useRef<SVGGElement>(null)
  const spinRef = useRef<Animation | null>(null)
  const settleRef = useRef<Animation | null>(null)

  // The spin is read and replaced by a settle rather than paused, because a paused spin would stop
  // wherever it happened to be.
  useEffect(() => {
    const rotor = rotorRef.current
    if (!rotor || typeof rotor.animate !== 'function') {
      return
    }
    const duration = spinDuration()

    if (isHeld) {
      const angle = spinRef.current ? angleOf(spinRef.current, duration) : 0
      spinRef.current?.cancel()
      spinRef.current = null
      const remaining = angle === 0 ? 0 : 360 - angle
      settleRef.current = rotor.animate([{ transform: `rotate(${angle}deg)` }, { transform: 'rotate(360deg)' }], {
        duration: Math.min(Math.max((remaining / 360) * duration, SETTLE_MIN_MS), SETTLE_MAX_MS),
        easing: 'cubic-bezier(0.25, 0.5, 0.5, 1)',
        fill: 'forwards',
      })
      return
    }

    settleRef.current?.cancel()
    settleRef.current = null
    spinRef.current = rotor.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], { duration, iterations: Infinity })
  }, [isHeld])

  useEffect(
    () => () => {
      spinRef.current?.cancel()
      settleRef.current?.cancel()
    },
    [],
  )

  return (
    <span className={classNames(styles.slot, 'position-relative d-inline-flex align-items-center justify-content-center flex-shrink-0')} aria-hidden='true'>
      {isSpinner ? (
        <span className={classNames(styles.spinner, 'position-absolute d-inline-flex')}>
          <Spinner size='sm' />
        </span>
      ) : null}
      <svg viewBox='0 0 24 24' className={classNames(styles.svg, { [styles.hidden]: isSpinner || isDone })}>
        <circle cx='12' cy='12' r={RADIUS} className={styles.track} />
        {/* Keeps turning while it fades to the check, so it never snaps back to 12 o'clock mid-fade. */}
        <g ref={rotorRef} className={styles.rotor}>
          <circle cx='12' cy='12' r={RADIUS} className={styles.arc} strokeDasharray={`${filled} ${CIRCUMFERENCE}`} />
        </g>
      </svg>
      <span className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'>
        <i className={classNames('bi bi-check-lg zocks-text-success lh-1 d-block', styles.check, { [styles.checkVisible]: isDone })} />
      </span>
    </span>
  )
}
